import React, { useLayoutEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import gsap from 'gsap';
import { submitFeedbackToSheet } from '../../../app/API';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import KwikMemoriesLogo from '../../../Assets/kwikmediaLogo.webp';
import { FaInstagram, FaFacebook, FaWhatsapp } from 'react-icons/fa';
import sendFirebaseEvent from '../../../analytics/FirebaseAnalytics';
import getInTouchPhoneLeft from '../../../Assets/getintouch/getintouch_phone1.webp';
import getInTouchPhoneRight from '../../../Assets/getintouch/getintouch_phone2.webp';
import { useNavigate } from 'react-router-dom';
import { FaPhoneAlt, FaEnvelope, FaCertificate, FaAward, FaShieldAlt } from 'react-icons/fa';
import { MdVerified } from 'react-icons/md';
import TrustSection from '../TrustSection/TrustSection';

gsap.registerPlugin(ScrollTrigger);

/* ---------------------------------------------------------
   Gradient definition for footer icons (rendered once, hidden)
   + global style forcing icon SVG paths to inherit that fill
--------------------------------------------------------- */
const IconGradientDef = () => (
  <svg width="0" height="0" style={{ position: 'absolute' }}>
    <defs>
      <linearGradient id="footer-icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%" gradientTransform="rotate(80)">
        <stop offset="0%" stopColor="#FFC951" />
        <stop offset="100%" stopColor="#C94DAA" />
      </linearGradient>
    </defs>
  </svg>
);

const FooterIconGlobalStyle = createGlobalStyle`
  .footer-icon svg,
  .footer-icon svg path {
    fill: inherit;
  }
`;

const NewFooter = () => {
  const navigate = useNavigate();
  const contactItems = [
    {
      label: '+91 9875082248',
      icon: <FaPhoneAlt />,
      href: 'tel:+919875082248',
      onClick: () => sendFirebaseEvent('contact', 'footer', 'click', 'contact_list_phone'),
    },
    {
      label: 'contact@kwikmemories.com',
      icon: <FaEnvelope />,
      href: 'mailto:contact@kwikmemories.com',
      onClick: () => sendFirebaseEvent('email', 'footer', 'click', 'contact_list_email'),
    },
    { label: 'Make in India Certification', icon: <MdVerified />, href: '#' },
    { label: 'ISO', icon: <FaShieldAlt />, href: '#' },
    { label: 'Govt. of India MSME', icon: <FaCertificate />, href: '#' },
    { label: 'Startup of India Certificate', icon: <FaAward />, href: '#' },
  ];

  const [feedbackName, setFeedbackName] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const bannerRef = useRef(null);
  const leftPhoneRef = useRef(null);
  const rightPhoneRef = useRef(null);

  const handleScroll = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else if (id === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSendFeedback = async () => {
    const message = (feedbackMessage || '').trim();
    const name = (feedbackName || '').trim();
    if (!message) return;

    sendFirebaseEvent('feedback', 'footer', 'click', 'send_feedback_button');

    setFeedbackSubmitting(true);
    setFeedbackSent(false);
    try {
      await submitFeedbackToSheet({ name, message, source: 'footer' });
      setFeedbackMessage('');
      setFeedbackName('');
      setFeedbackSent(true);
    } catch (err) {
      console.error('Failed to send feedback', err);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  useLayoutEffect(() => {
    const banner = bannerRef.current;
    const leftPhone = leftPhoneRef.current;
    const rightPhone = rightPhoneRef.current;

    if (!banner || !leftPhone || !rightPhone) return undefined;

    const mm = gsap.matchMedia();

    mm.add('(min-width: 769px)', () => {
      const ctx = gsap.context(() => {
        const bannerTextEls = banner.querySelectorAll('[data-banner-animate]');

        gsap.set(leftPhone, {
          x: 190,
          y: 130,
          rotate: -22,
          scale: 1.18,
          autoAlpha: 1,
          transformOrigin: '50% 50%',
        });
        gsap.set(rightPhone, {
          x: -190,
          y: -130,
          rotate: -28,
          scale: 1.18,
          autoAlpha: 1,
          transformOrigin: '50% 50%',
        });
        gsap.set(bannerTextEls, {
          y: 18,
          autoAlpha: 0,
          scale: 1,
          transformOrigin: '50% 50%',
        });

        const tl = gsap.timeline({ paused: true });

        tl.to(leftPhone, {
          x: 0,
          y: 0,
          rotate: -25,
          scale: 1,
          autoAlpha: 1,
          duration: 1,
          ease: 'power2.out',
        }, 0);

        tl.to(rightPhone, {
          x: 0,
          y: 0,
          rotate: -25,
          scale: 1,
          autoAlpha: 1,
          duration: 1,
          ease: 'power2.out',
        }, 0);

        tl.to(bannerTextEls, {
          y: 0,
          autoAlpha: 1,
          scale: 1,
          stagger: 0.12,
          ease: 'power2.out',
          duration: 0.8,
        }, 0.15);

        ScrollTrigger.create({
          trigger: banner,
          start: 'top 80%',
          onEnter: () => tl.play(),
          onEnterBack: () => tl.restart(),
          onLeaveBack: () => {
            tl.pause(0);
            gsap.set(leftPhone, { x: 190, y: 130, rotate: -22, scale: 1.18, autoAlpha: 1 });
            gsap.set(rightPhone, { x: -190, y: -130, rotate: -28, scale: 1.18, autoAlpha: 1 });
            gsap.set(bannerTextEls, { y: 18, autoAlpha: 0, scale: 1 });
          },
        });
      }, banner);

      return () => ctx.revert();
    });

    mm.add('(max-width: 768px)', () => {
      gsap.set([leftPhone, rightPhone, ...banner.querySelectorAll('[data-banner-animate]')], { clearProps: 'all' });
    });

    return () => mm.revert();
  }, []);

  return (
    <FooterContainer id="contactus">
      <FooterIconGlobalStyle />
      <IconGradientDef />

      <GradientBanner ref={bannerRef}>
        <CornerPhoneLeft ref={leftPhoneRef} src={getInTouchPhoneLeft} alt="Get in touch left phone" />
        <CornerPhoneRight ref={rightPhoneRef} src={getInTouchPhoneRight} alt="Get in touch right phone" />
        <BannerTitle data-banner-animate>Get in touch with us!</BannerTitle>
        <BannerDescription data-banner-animate>
          We're here to help! Whether you're a studio looking for support or a user with
          inquiries, feel free to reach out to us. Our team is ready to provide the
          guidance you need to make the most out of KwikMemories.
        </BannerDescription>
        <ContactInfoRow data-banner-animate>
          <ContactDetail
            onClick={() => {
              sendFirebaseEvent('contact', 'footer', 'click', 'banner_phone_detail');
              window.location.href = 'tel:+919875082248';
            }}
          >
            Contact : +91 9875082248
          </ContactDetail>
          <ContactDetail
            onClick={() => {
              sendFirebaseEvent('email', 'footer', 'click', 'banner_email_detail');
              window.location.href = 'mailto:contact@kwikmemories.com';
            }}
          >
            Email : contact@kwikmemories.com
          </ContactDetail>
        </ContactInfoRow>
        <ContactButton
  href="tel:+919875082248"
  data-banner-animate
  onClick={() => sendFirebaseEvent('contact', 'footer', 'click', 'banner_contact_button')}
>
  <ContactButtonText>Contact</ContactButtonText>
</ContactButton>
      </GradientBanner>

      <TrustSection />

      <Divider />

      <LinksBand>

        <TopBand>
          <LogoTaglineRow>
            <BrandLogo src={KwikMemoriesLogo} alt="KwikMemories" />
          </LogoTaglineRow>
          <Tagline>Run Your Studio. Don't Just Shoot It.</Tagline>
          <BrandBlurb>
            Kwik Memories is India's leading photography studio management app —
            helping thousands of studio owners automate bookings, payments, album delivery, and team management.
          </BrandBlurb>
        </TopBand>

        <LinkGroup>
          <GroupTitle>Product</GroupTitle>
          <ul>
            <li><FooterLink as="button" onClick={() => handleScroll('home')}>Hero</FooterLink></li>
            <li><FooterLink as="button" onClick={() => handleScroll('features')}>Features</FooterLink></li>
            <li><FooterLink as="button" onClick={() => handleScroll('tool-skill')}>Tool & Skill</FooterLink></li>
            <li><FooterLink as="button" onClick={() => handleScroll('powerful-feature')}>Core Feature</FooterLink></li>
            <li><FooterLink as="button" onClick={() => handleScroll('testimonials')}>Testimonial</FooterLink></li>
          </ul>
        </LinkGroup>

        <LinkGroup>
          <GroupTitle>For Studios</GroupTitle>
          <ul>
            {['Wedding Photography', 'Portrait Studios', 'Commercial Studios', 'Multi-Branch'].map(l => (
              <li key={l}><FooterLink href="#">{l}</FooterLink></li>
            ))}
          </ul>
        </LinkGroup>

        <LinkGroup>
          <GroupTitle>Resources</GroupTitle>
          <ul>
            {['Blog', 'Help Center', 'Studio Academy'].map(l => (
              <li key={l}><FooterLink href="#">{l}</FooterLink></li>
            ))}
          </ul>
        </LinkGroup>

        <LinkGroup>
          <GroupTitle>Company</GroupTitle>
          <ul>
            <li><FooterLink href="#">About</FooterLink></li>
            <li><FooterLink href="#">Careers</FooterLink></li>
            <li>
              <FooterLink
                as="button"
                onClick={() => {
                  sendFirebaseEvent('privacy_policy', 'footer', 'click', 'company_links');
                  navigate('/extra/privacy-policy');
                }}
              >
                Privacy Policy
              </FooterLink>
            </li>
            <li>
              <FooterLink
                as="button"
                onClick={() => {
                  sendFirebaseEvent('terms', 'footer', 'click', 'company_links');
                  navigate('/extra/Terms-and-conditions');
                }}
              >
                Terms
              </FooterLink>
            </li>
          </ul>
        </LinkGroup>

        <LinkGroup>
          <GroupTitle>Contact Us</GroupTitle>
          <ul>
            {contactItems.map(({ label, icon, href, onClick }) => (
              <li key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '12px' }}>
                <span className="footer-icon" style={{ fill: 'url(#footer-icon-gradient)' }}>{icon}</span>
                <FooterLink href={href} onClick={onClick}>{label}</FooterLink>
              </li>
            ))}
          </ul>
        </LinkGroup>

      </LinksBand>

      <Divider />

      <BottomBand>
        <SocialRow>
          <SocialLink
            href="https://www.instagram.com/kwikmemories/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            onClick={() => sendFirebaseEvent('social_click', 'footer', 'click', 'instagram')}
          >
            <FaInstagram />
          </SocialLink>
          <SocialLink
            href="https://www.facebook.com/share/17UFe2pPTm/?mibextid=wwXIfr"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            onClick={() => sendFirebaseEvent('social_click', 'footer', 'click', 'facebook')}
          >
            <FaFacebook />
          </SocialLink>
          <SocialLink
            href="https://wa.me/919875082248"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
            onClick={() => sendFirebaseEvent('social_click', 'footer', 'click', 'whatsapp')}
          >
            <FaWhatsapp />
          </SocialLink>
        </SocialRow>
        <SeoText>
          KwikMemories is India's all-in-one photography studio management Software App — Leads, Whatsapp CRM, Bookings, Payments, Team, Albums, and Premium client galleries, all in one place right from your phone. Built for Studios Owners. Built for Growth.
        </SeoText>
        <SearchContainer>
          <SearchBar>
            <SearchInput
              placeholder='Your name'
              value={feedbackName}
              onChange={(e) => setFeedbackName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && document.getElementById('footer-feedback-msg')?.focus()}
              disabled={feedbackSubmitting}
              style={{ flex: '0 0 100px' }}
            />
            <SearchInput
              id='footer-feedback-msg'
              placeholder='Send feedback'
              value={feedbackMessage}
              onChange={(e) => setFeedbackMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendFeedback()}
              disabled={feedbackSubmitting}
              style={{ flex: 1 }}
            />
            <SendButton
              onClick={handleSendFeedback}
              disabled={feedbackSubmitting || !feedbackMessage.trim()}
            >
              {feedbackSubmitting ? '...' : feedbackSent ? 'Sent' : 'Send'}
            </SendButton>
          </SearchBar>
        </SearchContainer>

      </BottomBand>
      <Copyright>
        © {new Date().getFullYear()} Kwik Memories. All rights reserved. Developed by The Coding Studio / The Sattva Tech, Surat.
      </Copyright>
    </FooterContainer>
  );
};

export default NewFooter;

const SearchContainer = styled.div`
  width: 20%;
  display: flex;
  align-items: center;
  justify-content: end;

  @media (max-width: 1000px) {
    width: 100%;
    justify-content: center;
  }
`;

const SearchBar = styled.div`
  width: 100%;
  max-width: 420px;
  height: 56px;
  padding: 8px 10px 8px 28px;
  border-radius: 30px;
  border: 2px solid transparent;
  background:
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(133.16deg, #FFC951 -4.52%, #CE56B0 107.63%) border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SearchInput = styled.input`
  border: none;
  font-family: Urbanist;
  font-size: 16px;
  font-weight: 500;
  min-width: 0;
  background-color: transparent;

  &:focus {
    outline: none;
  }
`;

const SendButton = styled.button`
  width: 80px;
  height: 40px;
  border-radius: 46px;
  background: linear-gradient(144.01deg, #FFC951 0%, #C94DAA 120%);
  color: white;
  border: none;
  cursor: pointer;
  font-family: Urbanist;
  font-size: 14px;
  font-weight: 600;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BrandLogo = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;

  @media (max-width: 900px) {
    width: 12px;
    height: 12px;
  }
`;

const FooterContainer = styled.footer`
  width: 100%;
  padding-top: 120px;
  background: #ffffff;
`;

const TopBand = styled.div`
  padding: 0px 4px 00px;
  display: flex;
  flex-direction: column;
  align-items: left;
  text-align: left;
  gap: 5px;
`;

const LogoTaglineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Tagline = styled.p`
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 2px;
  max-width: 170px;
  text-transform: uppercase;
  background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const BrandBlurb = styled.p`
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-size: 11px;
  line-height: 1.7;
  color: #020202ff;
  max-width: 200px;
  margin-top: 4px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f0f0f0;
  margin: 0 48px;

  @media (max-width: 768px) { margin: 0 24px; }
`;

const LinksBand = styled.div`
  padding: 20px 150px;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 32px 24px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }

  ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; }
`;

const LinkGroup = styled.div``;

const GroupTitle = styled.p`
  font-family: 'Gilroy-Bold', 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  margin: 0 0 14px 0;
  display: inline-block;
  background: linear-gradient(80deg, #FFC951 0%, #C94DAA 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const FooterLink = styled.a`
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-size: 12px;
  color: #000000ff;
  text-decoration: none;
  line-height: 1;
  transition: color 0.15s;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  &:hover {
    background: linear-gradient(140deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const BottomBand = styled.div`
  padding: 14px 80px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 900px) {
    flex-direction: column;
    padding: 24px;
    text-align: center;
  }
`;

const SocialRow = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 0;
`;

const SocialLink = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #facb64ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  font-size: 16px;
  text-decoration: none;
  transition: border-color 0.15s, color 0.15s;

  &:hover { border-color: #C94DAA; color: #C94DAA; }
`;

const SeoText = styled.p`
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-size: 12px;
  color: #aaa;
  line-height: 1.7;
  max-width: 90%;
  text-align: center;
  flex: 1;
`;

const Copyright = styled.p`
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-size: 14px;
  color: white;
  background: linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%);
  line-height: 3.5;
  text-align: center;
  flex-shrink: 0;
  max-width: 100%;
`;

const GradientBanner = styled.div`
  background: linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%);
  width: 100%;
  padding: 24px 40px 18px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 48px 20px 28px;
  }

  @media (max-width: 1024px) {
    padding: 32px 28px 12px;
  }
`;

const CornerPhoneLeft = styled.img`
  position: absolute;
  left: -60px;
  top: -80px;
  width: 280px;
  transform: rotate(-25deg);
  height: auto;
  object-fit: contain;
  pointer-events: none;
  z-index: 1;
  will-change: transform, opacity;

  @media (max-width: 1024px) {
    width: 180px;
    left: -58px;
    bottom: -38px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const CornerPhoneRight = styled.img`
  position: absolute;
  right: -60px;
  bottom: -80px;
  width: 280px;
  height: auto;
  transform: rotate(-25deg);
  object-fit: contain;
  pointer-events: none;
  z-index: 1;
  will-change: transform, opacity;

  @media (max-width: 1024px) {
    width: 180px;
    right: -58px;
    bottom: -38px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const BannerTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-style: italic;
  font-weight: 700;
  font-size: 48px;
  color: #ffffff;
  margin: 0 0 20px 0;
  line-height: 1.2;
  position: relative;
  z-index: 2;

  @media (max-width: 1024px) {
    font-size: 56px;
    margin-bottom: 14px;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const BannerDescription = styled.p`
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 21px;
  line-height: 145%;
  letter-spacing: 0;
  text-align: center;
  color: rgba(255, 255, 255, 1);
  max-width: 760px;
  margin: 0 0 20px 0;
  opacity: 0.95;
  position: relative;
  z-index: 2;

  @media (max-width: 1024px) {
    font-size: 18px;
    margin-bottom: 24px;
    max-width: 680px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 28px;
  }
`;

const ContactInfoRow = styled.div`
  display: flex;
  gap: 36px;
  margin-bottom: 20px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    margin-bottom: 32px;
  }

  @media (max-width: 1024px) {
    margin-bottom: 24px;
    gap: 28px;
  }
`;

const ContactDetail = styled.span`
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif;
  font-weight: 900;
  font-size: 18px;
  line-height: 145%;
  letter-spacing: 0;
  cursor: pointer;
  text-align: center;
  color: rgba(255, 255, 255, 1);

  /* Glass effect */
  display: inline-block;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 8px 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  @media (max-width: 1024px) {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContactButton = styled.a`
  background: #ffffff;
  border: none;
  border-radius: 999px;
  padding: 12px 48px;
  min-width: 180px;
  font-family: 'Urbanist', 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 22px;
  line-height: 183%;
  letter-spacing: 0;
  text-align: center;
  color: #121212;
  text-decoration: none;
  display: inline-block;
  cursor: pointer;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  z-index: 2;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0px 12px 28px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 12px 40px;
    min-width: 160px;
    font-size: 18px;
  }

  @media (max-width: 480px) {
    padding: 10px 28px;
    min-width: 140px;
    font-size: 16px;
  }
`;

const ContactButtonText = styled.span`
  background: linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`;
